import notification from 'antd/es/notification/index';
import type { ArgsProps, NotificationInstance } from 'antd/es/notification/interface';
import type { Key } from 'react';

import { useAntApp } from './AntApp';

const DTI = 'Notification';
const addDefaultParams = (args: ArgsProps) => {
  const draft = { ...args };

  if (!draft.props) {
    draft.props = {};
  } else {
    draft.props = { ...args.props };
  }

  // Add DTI
  if (!draft.props['data-testid']) {
    draft.props['data-testid'] = 'Notification';
  }

  // Add the type of notification
  if (!(draft.props as { 'data-testvalue'?: string })['data-testvalue']) {
    (draft.props as { 'data-testvalue'?: string })['data-testvalue'] = args.type || 'info';
  }

  if (!draft.style) {
    draft.style = {};
  } else {
    draft.style = { ...args.style };
  }

  // Add z-index
  if (draft.style.zIndex === 'undefined') {
    draft.style.zIndex = 'var(--z-index-notification)';
  }

  // Wrap title into DTI
  if (typeof draft.title === 'string') {
    draft.title = <span data-testid={`${DTI}__Message`}>{draft.title}</span>;
  }

  // Wrap description into DTI
  if (typeof draft.description === 'string') {
    draft.description = <span data-testid={`${DTI}__Description`}>{draft.description}</span>;
  }

  // Set default duration
  if (typeof draft.duration === 'undefined') {
    draft.duration = 3;
  }

  return draft;
};

const getNotification = (notificationInstance: NotificationInstance) => {
  const error = (args: Omit<ArgsProps, 'type'>) =>
    notificationInstance.error(addDefaultParams({ ...args, type: 'error' }));
  const success = (args: Omit<ArgsProps, 'type'>) =>
    notificationInstance.success(addDefaultParams({ ...args, type: 'success' }));
  const info = (args: Omit<ArgsProps, 'type'>) =>
    notificationInstance.info(addDefaultParams({ ...args, type: 'info' }));
  const open = (args: Omit<ArgsProps, 'type'>) => notificationInstance.open(addDefaultParams(args));
  const destroy = (key?: Key) => notificationInstance.destroy(key);

  return {
    error,
    success,
    info,
    open,
    destroy,
  };
};

export const useAntNotification = () => {
  const { notification } = useAntApp();

  return getNotification(notification);
};

/** Use this hook when you need to render a ReactRouter link in the notification */
export const useAntNotificationWithContext = () => {
  const [api, contextHolder] = notification.useNotification();

  return [getNotification(api), contextHolder] as const;
};
